import { ExperienceEditor } from '@/components/admin/experiences/experience-editor';
import { readExperienceEditorSearch } from '@/lib/experience-search';
import { createFileRoute, useNavigate } from '@tanstack/react-router';

/**
 * L'expérience neuve - l'éditeur vide (refonte modulaire §4, §9.1; issue #55).
 *
 * ## It calls nothing of its OWN
 *
 * « La carte en pointillé ouvre l'éditeur vide » (§4). Before that first save
 * there is neither entity nor server draft, so what is typed lives only in the
 * page - which is also why this is a route of its own rather than
 * `/admin/experiences/$experienceId` with a sentinel id: an editor that had to
 * answer « is this id real » on every write would carry the branch in nine
 * places instead of in one `detail === null`.
 *
 * The editor it mounts DOES read the two reference libraries, and that is not a
 * contradiction of the paragraph above: nothing of the experience exists yet,
 * but the Persona tab offers personas to choose from the first click, and a
 * chooser with nothing in it would be a screen that looks broken.
 *
 * ## The template of §5.4 is what it opens on
 *
 * `newExperienceContent()` seeds the draft with `[Consentement et identité] ->
 * [Protocole]`, so the Timeline tab shows two pages before anything is typed
 * (§9.1). The guided form of §13 - which also applies that template - is lot 7;
 * this is the régime of §4, « la carte en pointillé ouvre l'éditeur vide ».
 *
 * The static segment wins over the dynamic one in TanStack Router, so
 * `/admin/experiences/nouveau` can never be read as an experience called
 * « nouveau ».
 *
 * `onView` and `onCloseView` are no-ops here, and that is honest rather than
 * lazy: there is no history to open a version from, so the gesture cannot be
 * reached - the Historique tab renders its « Aucune version » sentence instead
 * of a single line.
 */

export const Route = createFileRoute('/admin/experiences/nouveau')({
  validateSearch: readExperienceEditorSearch,
  component: NouvelleExperiencePage,
});

function NouvelleExperiencePage() {
  const { tab, page } = Route.useSearch();
  const navigate = useNavigate();

  return (
    <ExperienceEditor
      detail={null}
      viewing={null}
      tab={tab}
      pageId={page ?? null}
      onSelectTab={(next) =>
        void navigate({ to: '/admin/experiences/nouveau', search: { tab: next }, replace: true })
      }
      onOpenPlace={(place) =>
        void navigate({
          to: '/admin/experiences/nouveau',
          search:
            place.pageId === null ? { tab: place.tab } : { tab: place.tab, page: place.pageId },
        })
      }
      onView={() => undefined}
      onCloseView={() => undefined}
      onReload={() => undefined}
    />
  );
}
